import {
    PermissionKind,
    PermissionModule,
    TotadminPermission,
    type PermissionCatalogEntryFragmentFragment,
} from 'data/__generated__';

export type CatalogEntry = PermissionCatalogEntryFragmentFragment;

export type ResourceRow = {
    resource: string;
    label: string;
    read: CatalogEntry | null;
    manage: CatalogEntry | null;
    actions: CatalogEntry[];
};

export type ModuleSection = {
    module: PermissionModule;
    label: string;
    rows: ResourceRow[];
    codes: string[];
};

/** Ordre et libellés d'affichage. Un `Record` : un module ajouté côté API sans libellé ici ne compile pas. */
const MODULE_LABELS: Record<PermissionModule, string> = {
    [PermissionModule.Tech]: 'Accès',
    [PermissionModule.Catalog]: 'Catalogue',
    [PermissionModule.Purchase]: 'Achats',
    [PermissionModule.Stock]: 'Entrepôt',
    [PermissionModule.Site]: 'Sites',
    [PermissionModule.Planogram]: 'Planogramme',
    [PermissionModule.Delivery]: 'Livraisons',
    [PermissionModule.Organization]: 'Clients',
    [PermissionModule.Order]: 'Commandes',
    [PermissionModule.Finance]: 'Finance',
    [PermissionModule.Engagement]: 'Engagement',
    [PermissionModule.Equipment]: 'Équipement',
    [PermissionModule.Settings]: 'Paramètres',
    [PermissionModule.User]: 'Utilisateurs et droits',
};

const MODULE_ORDER = Object.keys(MODULE_LABELS) as PermissionModule[];

/** Les droits fins seuls ont une place dans la grille : les features historiques n'ont ni ressource ni colonne. */
export const isGridEntry = (entry: CatalogEntry): boolean => !!entry.resource && !!entry.kind;

export function buildSections(catalog: CatalogEntry[]): ModuleSection[] {
    const rowsByModule = new Map<PermissionModule, Map<string, ResourceRow>>();

    for (const entry of catalog.filter(isGridEntry)) {
        const rows = rowsByModule.get(entry.module) ?? new Map<string, ResourceRow>();
        const resource = entry.resource!;
        const row = rows.get(resource) ?? {
            resource,
            label: entry.resourceLabel ?? resource,
            read: null,
            manage: null,
            actions: [],
        };

        if (entry.kind === PermissionKind.Read) row.read = entry;
        else if (entry.kind === PermissionKind.Manage) row.manage = entry;
        else row.actions.push(entry);

        rows.set(resource, row);
        rowsByModule.set(entry.module, rows);
    }

    return MODULE_ORDER.filter((module) => rowsByModule.has(module)).map((module) => {
        const rows = [...rowsByModule.get(module)!.values()];
        return {
            module,
            label: MODULE_LABELS[module],
            rows,
            codes: rows.flatMap(rowCodes),
        };
    });
}

export const rowCodes = ({ read, manage, actions }: ResourceRow): string[] =>
    [read, manage, ...actions].filter((entry): entry is CatalogEntry => !!entry).map(({ code }) => code);

/**
 * Cocher un droit, avec les deux dépendances que l'API ne vérifie pas mais sans lesquelles le groupe
 * serait inutilisable :
 * - gérer (ou une action) sans consulter ouvrirait une page d'édition qu'on ne peut pas charger ;
 * - n'importe quel droit sans `totadmin_access` : les membres ne pourraient même pas se connecter.
 */
export function grant(selected: readonly string[], codes: readonly string[], rows: ResourceRow[]): string[] {
    const next = new Set([...selected, ...codes]);

    for (const row of rows) {
        const hasWrite = [row.manage, ...row.actions].some((entry) => entry && next.has(entry.code));
        if (hasWrite && row.read) next.add(row.read.code);
    }

    if (next.size > 0) next.add(TotadminPermission.TotadminAccess);

    return [...next];
}

/** Décocher la lecture retire aussi ce qui en dépend sur la même ressource. */
export function revoke(selected: readonly string[], codes: readonly string[], rows: ResourceRow[]): string[] {
    const removed = new Set(codes);

    for (const row of rows) {
        if (row.read && removed.has(row.read.code)) rowCodes(row).forEach((code) => removed.add(code));
    }

    return selected.filter((code) => !removed.has(code));
}

export type CheckState = boolean | 'indeterminate';

export const getCheckState = (selected: ReadonlySet<string>, codes: readonly string[]): CheckState => {
    const count = codes.filter((code) => selected.has(code)).length;
    if (count === 0) return false;
    return count === codes.length ? true : 'indeterminate';
};
